import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth/AuthContext';
import { useSettings } from '@/features/settings/contexts/SettingsContext';
import { cn, useTheme } from '@/ui/theme/ThemeContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  resource?: string;
  action?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  resource, 
  action = 'view' 
}) => {
  const { isAuthenticated, isLoading, hasPermission } = useAuth();
  const { t } = useSettings();
  // Follow the active theme; the static light palette flashed a bright screen during dark-mode starts.
  const { theme } = useTheme();
  const location = useLocation();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className={cn('min-h-screen flex items-center justify-center px-6', theme.bgPrimary)}>
        <div className="max-w-sm text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" aria-hidden="true"></div>
          <h1 className={cn('mt-5 text-lg font-semibold', theme.textPrimary)}>
            {t('Preparing Management Tool')}
          </h1>
          <p className={cn('mt-2 text-sm leading-6', theme.textSecondary)}>
            {t('We are checking your session, loading project access, and preparing the workspace controls. If this takes too long, continue to sign in or open feedback support.')}
          </p>
          <div className="mt-5 flex items-center justify-center gap-2">
            <button
              type="button"
              className={cn('rounded border px-4 py-3 text-sm', theme.borderPrimary, theme.textPrimary)}
              onClick={() => {
                window.location.assign('/login');
              }}
            >
              {t('Continue to sign in')}
            </button>
            <button
              type="button"
              className={cn('rounded border px-4 py-3 text-sm', theme.borderPrimary, theme.textPrimary)}
              onClick={() => {
                window.location.assign('/login?feedback=1');
              }}
            >
              {t('Feedback support')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Redirect to sign in if not authenticated
  if (!isAuthenticated) {
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} state={{ from: location }} replace />;
  }

  // Check permissions if resource is specified
  if (resource && !hasPermission(resource, action)) {
    return (
      <div className={cn('min-h-screen flex items-center justify-center', theme.bgPrimary)}>
        <div className="text-center">
          <h2 className={cn('text-2xl font-bold mb-2', theme.textPrimary)}>
            {t('Access Denied')}
          </h2>
          <p className={theme.textSecondary}>
            {t("You don't have permission to access this resource.")}
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
